import type { SuretyAccountId } from "@inrev/common";
import { useAdminUpdateSuretyAccountBondingLine } from "@portal/api/account/admin";
import { cn } from "@portal/components/lib/utils";
import { Button, Icon, Text } from "@portal/components/primitives";
import { PortaledTooltip } from "@portal/components/ui/Tooltip";
import { useFlags } from "@portal/providers/GlobalFlagsProvider";
import { useCallback, useId } from "react";
import { buildPublishPayload } from "./payload";
import { useBondingLineDispatch, useBondingLineState } from "./state/BondingLineStateProvider";

type BondingLinePublishControlProps = {
	accountId: SuretyAccountId;
};

export const BondingLinePublishControl = ({ accountId }: BondingLinePublishControlProps) => {
	const state = useBondingLineState();
	const dispatch = useBondingLineDispatch();
	const { showErrorFlag } = useFlags();
	const { updateSuretyAccountBondingLine } = useAdminUpdateSuretyAccountBondingLine(accountId);
	const isPublished = state.inrev.isPublished;
	const tooltipId = useId();

	// Readonly commits the last-saved line; review/editing commits the staged draft.
	const singleLimit =
		state.mode === "readonly" ? state.baseline.inrev.singleLimit : state.inrev.singleLimit;
	const publishBlocked = !isPublished && (singleLimit === null || singleLimit <= 0);

	const handleToggle = useCallback(() => {
		if (publishBlocked) return;
		const nextIsPublished = !state.inrev.isPublished;

		// In review/editing the publish flag is staged and committed by the Approve/Save
		// payload, so flip it locally and let that flow persist it.
		if (state.mode !== "readonly") {
			dispatch({ type: "toggle_publish" });
			return;
		}

		// Readonly persists immediately. Validate before flipping: once the optimistic flip
		// makes state dirty, the provider's reconciliation guard refuses to correct it from a
		// refetch, so a flip we don't (or can't) persist would leave the toggle stuck.
		const payload = buildPublishPayload(state, nextIsPublished);
		if (!payload) {
			showErrorFlag({
				heading: "Couldn't update visibility",
				message: "The saved bonding line is incomplete. Edit its rate and limits, then try again.",
			});
			return;
		}

		dispatch({ type: "toggle_publish" });
		updateSuretyAccountBondingLine(payload, {
			onError: () => {
				dispatch({ type: "toggle_publish" }); // revert the optimistic flip
				showErrorFlag({
					heading: "Couldn't update visibility",
					message: "Something went wrong saving the change. Please try again.",
				});
			},
		});
	}, [publishBlocked, state, dispatch, updateSuretyAccountBondingLine, showErrorFlag]);

	return (
		<>
			<Button
				variant="ghost"
				role="switch"
				aria-checked={isPublished}
				aria-disabled={publishBlocked || undefined}
				onClick={handleToggle}
				data-testid="publish-toggle"
				data-tooltip-id={tooltipId}
				data-tooltip-content={
					publishBlocked ? "Set a single limit above $0 to publish this line." : undefined
				}
				aria-label={isPublished ? "Unpublish inRev bonding line" : "Publish inRev bonding line"}
				trackingId="account-admin-bonding-lines-publish"
				className={cn(
					"inline-flex items-center gap-x-[5px] h-auto px-0",
					publishBlocked ? "cursor-not-allowed opacity-[0.45]" : "cursor-pointer",
				)}
				leadingIcon={
					<Icon
						type={isPublished ? "eye" : "eye-slash"}
						size="mini"
						color={isPublished ? "confirm" : "tertiary"}
					/>
				}
			>
				<Text type="caption" color={isPublished ? "confirm" : "tertiary"}>
					{isPublished ? "Published" : "Unpublished"}
				</Text>
			</Button>
			{publishBlocked && <PortaledTooltip id={tooltipId} style={{ maxWidth: 260 }} />}
		</>
	);
};
